import React, {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { css } from '@emotion/react'
import cn from 'classnames'

import { selectIsEditorPinned } from 'ducks/editor/selectors'

import ScrollerContext from 'components/Scroller/Context'
import editor from 'shared/editor/utils'

import SizerContext from './Context'

const detectorStyles = css`
  user-select: none;
  pointer-events: none;
  visibility: hidden;
  height: 0px;
  width: 100%;
`

const ROOT_MARGIN_PX = -8
const BOTTOM_DETECTOR_OFFSET_PX = ROOT_MARGIN_PX - 1

const bottomDetectorStyle = css`
  margin-top: -${BOTTOM_DETECTOR_OFFSET_PX}px;
`

const queueFunction = window.requestIdleCallback || window.requestAnimationFrame
const cancelQueueFunction =
  window.cancelIdleCallback || window.cancelAnimationFrame
const queueOptions = window.requestIdleCallback ? { timeout: 500 } : undefined

const Sizer = React.forwardRef(({ children, className, footerRef }, ref) => {
  const { getScrollerAPI } = useContext(ScrollerContext)
  const cursorQueueIdRef = useRef(null)
  const floatingQueueIdRef = useRef(null)
  const iObserverRef = useRef(null)
  const innerRef = useRef(null)
  const topDetectorRef = useRef(null)
  const bottomDetectorRef = useRef(null)
  const isEditorPinned = useSelector(selectIsEditorPinned)
  useImperativeHandle(ref, () => innerRef.current)

  const handleIntersection = useCallback(
    entry => {
      const topDetector = topDetectorRef.current
      const bottomDetector = bottomDetectorRef.current
      const inner = innerRef.current

      if (!topDetector || !bottomDetector || !inner) return

      switch (entry.target) {
        case topDetector:
          floatingQueueIdRef.current = queueFunction(() => {
            inner.classList.toggle('floatingHeader', !entry.isIntersecting)
          }, queueOptions)
          break
        case bottomDetector:
          floatingQueueIdRef.current = queueFunction(() => {
            inner.classList.toggle('floatingFooter', !entry.isIntersecting)
          }, queueOptions)
          break
        default:
          throw new Error("Unhandled entry in Sizer's IntersectionObserver")
      }
    },
    [topDetectorRef, bottomDetectorRef, floatingQueueIdRef, innerRef]
  )

  useEffect(
    () => {
      const container = getScrollerAPI()?.getElement()
      let iObserver = iObserverRef.current
      if (container && !iObserver && window.IntersectionObserver) {
        iObserver = new window.IntersectionObserver(
          entries => {
            entries.forEach(handleIntersection)
          },
          {
            root: container,
            threshold: [0, 1],
            rootMargin: `${ROOT_MARGIN_PX}px`,
          }
        )
        iObserverRef.current = iObserver
      }

      return () => {
        if (iObserver) {
          iObserver.disconnect()
        }
      }
    },
    [iObserverRef, getScrollerAPI, handleIntersection]
  )

  useEffect(
    () => {
      const iObserver = iObserverRef.current
      if (!iObserver) return
      const topDetector = topDetectorRef.current
      const bottomDetector = bottomDetectorRef.current
      if (topDetector) iObserver.observe(topDetector)
      if (bottomDetector) iObserver.observe(bottomDetector)
    },
    [iObserverRef, topDetectorRef, bottomDetectorRef]
  )

  const scrollIntoView = useCallback(
    (onlyIfNeeded = false) => {
      if (isEditorPinned) return
      const scrollerAPI = getScrollerAPI()
      const bottomDetector = bottomDetectorRef.current
      if (scrollerAPI && bottomDetector) {
        const { containerHeight, scrollTop } = scrollerAPI.getScrollDimensions()
        if (onlyIfNeeded) {
          const inner = innerRef.current
          if (inner) {
            const center = inner.offsetTop + inner.offsetHeight / 2
            if (center < scrollTop + containerHeight) {
              return
            }
          }
        }
        const y =
          bottomDetector.offsetTop -
          containerHeight +
          Math.abs(BOTTOM_DETECTOR_OFFSET_PX)
        scrollerAPI.scrollToY(y)
      }
    },
    [getScrollerAPI, bottomDetectorRef, innerRef, isEditorPinned]
  )

  const handleEditorInit = useCallback(
    () => {
      // HACK (jscheel): We have to defer to let the render loop run first
      setTimeout(() => {
        scrollIntoView()
      }, 1)
    },
    [scrollIntoView]
  )

  const handleEditorChange = useCallback(
    () => {
      // NOTE (jscheel): This ensures that the cursor is fully in view when a
      // a user types or clicks. It tries to grab the cursor's rect and the
      // footer's rect, and adjusts the scroll position if there is an overlap.
      cursorQueueIdRef.current = window.requestAnimationFrame(() => {
        const activeEditor = editor.getEditor()
        const scrollerAPI = getScrollerAPI()
        if (!scrollerAPI) return
        const container = scrollerAPI.getElement()
        if (!activeEditor || !container) return
        const range = activeEditor.selection?.getRng()
        const rects = range.getClientRects()
        if (!rects) return
        let selectionRect = rects[rects.length - 1]

        // HACK (jscheel): Get selection's closest ancestor if we can't calculate
        // the bounding rect from the current selection.
        if (
          !selectionRect ||
          (selectionRect.width === 0 && selectionRect.height === 0)
        ) {
          selectionRect = activeEditor.selection
            ?.getRng()
            ?.commonAncestorContainer?.getBoundingClientRect()
        }

        const actionsRect = footerRef.current?.getBoundingClientRect()
        if (!selectionRect || !actionsRect) return
        const overlap = selectionRect.bottom - actionsRect.top
        if (overlap > 0) {
          const newY = container.scrollTop + Math.round(overlap) + 10
          scrollerAPI.scrollToY(newY)
        }
      })
    },
    [cursorQueueIdRef, footerRef, getScrollerAPI]
  )

  useEffect(
    () => {
      const activeEditor = editor.getEditor()
      if (activeEditor) {
        activeEditor.on('init', handleEditorInit)
        activeEditor.on('focus', handleEditorChange)
        activeEditor.on('change', handleEditorChange)
        activeEditor.on('keyup', handleEditorChange)
        activeEditor.once('SetContent', handleEditorChange)
      }

      return () => {
        if (activeEditor) {
          activeEditor.on('init', handleEditorInit)
          activeEditor.on('off', handleEditorChange)
          activeEditor.off('change', handleEditorChange)
          activeEditor.off('keyup', handleEditorChange)
          activeEditor.off('SetContent', handleEditorChange)
        }
      }
    },
    [handleEditorChange, handleEditorInit]
  )

  useEffect(
    () => {
      const cursorQueueId = cursorQueueIdRef.current
      const floatingQueueId = floatingQueueIdRef.current
      return () => {
        if (cursorQueueId) cancelQueueFunction(cursorQueueId)
        if (floatingQueueId) cancelQueueFunction(floatingQueueId)
      }
    },
    [cursorQueueIdRef, floatingQueueIdRef]
  )

  const context = useMemo(
    () => {
      return { scrollIntoView }
    },
    [scrollIntoView]
  )

  return (
    <SizerContext.Provider value={context}>
      <div ref={innerRef} className={cn('inner', className)}>
        <div ref={topDetectorRef} css={detectorStyles} />
        {children}
        <div
          ref={bottomDetectorRef}
          css={[detectorStyles, bottomDetectorStyle]}
        />
      </div>
    </SizerContext.Provider>
  )
})

export default Sizer
